<template>
  <div>
    <section
      class="relative flex min-h-[34rem] w-full items-end overflow-hidden pb-8 md:min-h-[35vh] md:pb-10"
    >
      <NuxtImg
        src="/images/bg/2026-04-23-search-hero-dreamland-v3.webp"
        alt=""
        aria-hidden="true"
        loading="eager"
        fetchpriority="high"
        width="2528"
        height="1042"
        format="webp"
        sizes="sm:100vw md:100vw lg:1920px"
        class="absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden="true"
        class="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-slate-900/10"
      />
      <div class="container relative z-10">
        <div class="text-white">
          <BookingSearchForm theme="search-hero" />
        </div>
      </div>
    </section>

    <section class="container py-16">
      <!-- Catalog mode -->
      <div v-if="!hasFullParams" class="flex flex-col gap-4">
        <ApartmentRow
          v-for="item in catalogItems"
          :key="item.listing_id"
          mode="catalog"
          :apartment="item"
          :catalog-price="fromPrices[item.listing_id] ?? null"
          :guest-facts="guestFactsMap[item.listing_id] ?? null"
          :locale="locale"
          :whatsapp-number="whatsappNumber"
        />
      </div>

      <!-- Quote mode -->
      <template v-else>
        <div v-if="searchPending" class="text-slate-500">
          {{ $t('quote.loading') }}
        </div>
        <div v-else-if="searchError" class="text-red-500">
          {{ $t('quote.errorGeneric') }}
        </div>
        <div v-else-if="!quoteItems.length" class="text-center py-16">
          <p class="text-xl text-slate-500">{{ $t('searchPage.noResults') }}</p>
          <BookingAskQuestionCta
            class="mt-5 justify-center"
            placement="search_no_results"
            :checkin="from || undefined"
            :checkout="to || undefined"
            :guests="guests || undefined"
          />
        </div>
        <div v-else class="flex flex-col gap-4">
          <ApartmentRow
            v-for="(item, i) in quoteItems"
            :key="`${item.apartment.listing_id}-${i}`"
            mode="quote"
            :apartment="item.apartment"
            :quote="item.quote"
            :guest-facts="guestFactsMap[item.apartment.listing_id] ?? null"
            :locale="locale"
            :whatsapp-number="whatsappNumber"
          />
          <BookingAskQuestionCta
            class="justify-center pt-1"
            placement="search_results"
            :checkin="from || undefined"
            :checkout="to || undefined"
            :guests="guests || undefined"
          />
        </div>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type SearchResult = {
  listingId: number;
  title: string;
  capacity: number;
  quote: {
    available: boolean;
    reason?: 'capacity' | 'dates_booked' | 'invalid_dates' | 'no_pricing';
    nights: number;
    tier: 'daily' | 'weekly';
    subtotalUsd: number;
    cleaningUsd: number;
    prepaymentUsd: number;
    extraGuestUsd: number;
    totalUsd: number;
    poolIncluded: boolean;
  };
};

type ContentApartment = {
  listing_id: number | string;
  title: string;
  cover_photo?: Array<{ url: string; alt?: string }>;
  location?: string;
  capacity?: number | string;
  rooms?: number | string;
  area_sqm?: number;
  view_type?: 'sea' | 'panoramic' | 'mountain';
  floor?: number | string;
  description?: string;
  extra_bed_available?: boolean;
};

const { locale, t } = useI18n();
const route = useRoute();
const config = useRuntimeConfig();
const whatsappNumber = config.public.whatsappNumber as string;

const from = computed(() => String(route.query.from ?? ''));
const to = computed(() => String(route.query.to ?? ''));
const guests = computed(() => Number(route.query.guests ?? 0));
const hasFullParams = computed(
  () => from.value !== '' && to.value !== '' && guests.value > 0,
);

// Apartment content — titles, cover_photo, location, facts. Needed in both modes.
const { data: apartmentsContent } = await useAsyncData(
  `apartments_search-${locale.value}`,
  async () => {
    const collection = ('apartments_' + locale.value) as 'apartments_en';
    return await queryCollection(collection).all();
  },
  { watch: [locale] },
);

// Baseline `from $X/night` prices — catalog mode
const { data: fromPricesData } = await useFetch<Record<number, number>>(
  '/api/apartments/from-prices',
  { default: () => ({}) },
);
const fromPrices = computed(() => fromPricesData.value ?? {});

// Guest-facts (capacity + extraGuestThreshold) for card guests block
type GuestFacts = { capacity: number; threshold: number; extraGuestUsd?: number };
const { data: factsData } = await useFetch<Record<number, GuestFacts>>(
  '/api/apartments/facts',
  { default: () => ({}) },
);
const guestFactsMap = computed(() => factsData.value ?? {});

// Quote fetch — only when full params present
const {
  data: searchData,
  pending: searchPending,
  error: searchError,
} = await useAsyncData(
  () => `search-${from.value}-${to.value}-${guests.value}`,
  async () => {
    if (!hasFullParams.value) return null;
    return await $fetch<{
      from: string;
      to: string;
      guests: number;
      results: SearchResult[];
    }>('/api/search', {
      query: { from: from.value, to: to.value, guests: guests.value },
    });
  },
  { watch: [from, to, guests] },
);

const mapContentToApartment = (a: ContentApartment) => ({
  listing_id: Number(a.listing_id),
  title: a.title,
  cover_photo: a.cover_photo?.[0] ?? {
    url: '/images/placeholder-image.jpg',
    alt: 'placeholder-image',
  },
  location: a.location || 'Chakvi',
  capacity: a.capacity,
  rooms: a.rooms,
  area_sqm: a.area_sqm,
  view_type: a.view_type,
  floor: a.floor,
  description: a.description,
  extra_bed_available: a.extra_bed_available,
});

// Catalog items — content-driven, widened to include facts for row layout
const catalogItems = computed(() =>
  (apartmentsContent.value ?? [])
    // Hide apartments not publicly visible. `guestFactsMap` is keyed by the
    // server's active && listed set, so its keys are the catalog allowlist.
    .filter((a) => guestFactsMap.value[Number((a as ContentApartment).listing_id)])
    .map((a) => mapContentToApartment(a as ContentApartment)),
);

// Quote items — merge API results with content (title, cover, facts)
const quoteItems = computed(() => {
  const results = searchData.value?.results ?? [];
  const byId = new Map<number, ContentApartment>(
    (apartmentsContent.value ?? []).map((a) => {
      const ca = a as ContentApartment;
      return [Number(ca.listing_id), ca];
    }),
  );
  return results.map((r) => {
    const content = byId.get(r.listingId);
    return {
      apartment: content
        ? mapContentToApartment(content)
        : {
            listing_id: r.listingId,
            title: r.title,
            cover_photo: {
              url: '/images/placeholder-image.jpg',
              alt: 'placeholder-image',
            },
            location: 'Chakvi',
          },
      quote: {
        available: r.quote.available,
        reason: r.quote.reason,
        nights: r.quote.nights,
        tier: r.quote.tier,
        subtotalUsd: r.quote.subtotalUsd,
        cleaningUsd: r.quote.cleaningUsd,
        prepaymentUsd: r.quote.prepaymentUsd,
        extraGuestUsd: r.quote.extraGuestUsd,
        totalUsd: r.quote.totalUsd,
        poolIncluded: r.quote.poolIncluded,
      },
    };
  });
});

useHead({
  title: () => t('searchPage.pageTitle'),
});

useSeoMeta({
  robots: 'noindex, follow',
});
</script>
